/**
 * Top-level view: the boot screen until boot finishes (and its minimum display
 * time passes or Esc is pressed), then the editor, in the layout the screen
 * calls for (the dock, or the compact layout on phones).
 */
import { useEffect, useState } from "react";
import type { Clock } from "../core/Clock";
import type { Log } from "../core/Log";
import type { BootMonitor } from "../editor/boot/BootMonitor";
import { shouldDismissBoot } from "../editor/boot/BootMonitor";
import { BootScreen } from "../editor/boot/BootScreen";
import { EditorProvider } from "../editor/EditorContext";
import type { EditorServices } from "../editor/EditorContext";
import { useStore } from "../editor/hooks/useStore";
import { CompactShell } from "../editor/shell/compact/CompactShell";
import { EditorShell } from "../editor/shell/EditorShell";
import type { ReadableStore } from "../core/store";
import type { EditorLayout, ShellProfile } from "./environment";

/** What the editor needs once boot has finished. */
export interface AppEditor {
  readonly services: EditorServices;
  /** Offer the first-run tour (A-25), with what to do when it's declined. */
  readonly tourOffer: { readonly onClose: (dontShowAgain: boolean) => void } | null;
}

export interface AppProps {
  readonly log: Log;
  readonly monitor: BootMonitor;
  readonly clock: Clock;
  readonly profile: ShellProfile;
  readonly layout: ReadableStore<EditorLayout>;
  readonly bootStartedMs: number;
  readonly minBootDisplayMs: number;
  readonly editor: AppEditor | null;
}

export function App(props: AppProps) {
  const dismissed = useBootDismissal(props);
  const layout = useStore(props.layout);

  if (!dismissed || !props.editor) {
    return (
      <BootScreen
        log={props.log}
        monitor={props.monitor}
        title="kn.engine"
        subtitle={[props.profile.name, props.profile.headline].filter(Boolean).join(" · ")}
        resumeHref={props.profile.resumeHref}
      />
    );
  }

  return (
    <EditorProvider services={props.editor.services}>
      {layout === "compact" ? (
        <CompactShell tourOffer={props.editor.tourOffer} />
      ) : (
        <EditorShell tourOffer={props.editor.tourOffer} />
      )}
    </EditorProvider>
  );
}

/** True once boot is ready and the minimum display time has passed (or Esc or a tap skipped it). */
function useBootDismissal({ monitor, clock, bootStartedMs, minBootDisplayMs }: AppProps): boolean {
  const boot = useStore(monitor);
  const [skipped, setSkipped] = useState(false);
  // Already elapsed (or no minimum, e.g. reduced motion) → no timer needed.
  const [timeUp, setTimeUp] = useState(
    () => remainingMs(clock, bootStartedMs, minBootDisplayMs) <= 0,
  );

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setSkipped(true);
    };
    // A phone has no Esc key: a tap on the boot screen skips its minimum instead.
    const onTap = (event: PointerEvent) => {
      if (event.pointerType === "touch") setSkipped(true);
    };
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerup", onTap);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerup", onTap);
    };
  }, []);

  useEffect(() => {
    if (timeUp) return;
    const timer = window.setTimeout(
      () => {
        setTimeUp(true);
      },
      remainingMs(clock, bootStartedMs, minBootDisplayMs),
    );
    return () => {
      window.clearTimeout(timer);
    };
  }, [timeUp, clock, bootStartedMs, minBootDisplayMs]);

  return shouldDismissBoot({
    ready: boot.phase === "ready",
    elapsedMs: timeUp ? minBootDisplayMs : 0,
    minDisplayMs: minBootDisplayMs,
    skipped,
  });
}

function remainingMs(clock: Clock, startedMs: number, minDisplayMs: number): number {
  return minDisplayMs - (clock.now() - startedMs);
}
